import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  EditOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpFilledOutlined,
  MoreVertOutlined,
  UploadFileOutlined,
} from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { Row } from "@tanstack/react-table"
import type { FC, ReactElement } from "react"
import { useRef, useState } from "react"

import type { Order } from "@/graphql/codegen/graphql"
import { OrderType } from "@/graphql/codegen/graphql"

interface PurchaseOrderActionsProps {
  row: Row<Order>
  purchaseOrderId: number
  canUpload: boolean
  onUploadClick: (orderId: string, orderType?: OrderType) => void
  orderType?: OrderType
}

export const PurchaseOrderActions: FC<PurchaseOrderActionsProps> = ({
  row,
  purchaseOrderId,
  onUploadClick,
  canUpload,
  orderType,
}): ReactElement => {
  const navigate = useNavigate()
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const handleOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }

  const handlePoUpdate = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate({
      to: "/app/procurement/purchase-orders/$purchaseOrderId",
      params: {
        purchaseOrderId: purchaseOrderId.toString(),
      },
    })
  }

  const handleFileAttach = (e: React.MouseEvent) => {
    e.stopPropagation()
    onUploadClick(purchaseOrderId.toString(), orderType)
  }

  return (
    <Box className='flex items-center justify-end space-x-4'>
      <Box className='flex items-center justify-end' ref={anchorElRef}>
        <IconButton onClick={handleOpen}>
          <MoreVertOutlined />
        </IconButton>
        <Menu
          anchorEl={anchorElRef.current}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            onClick={(e) => {
              handlePoUpdate(e)
            }}
          >
            <ListItemIcon>
              <EditOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            </ListItemIcon>
            <ListItemText primary='View Details' />
          </MenuItem>
          {canUpload && (
            <MenuItem
              onClick={(e) => {
                handleFileAttach(e)
              }}
            >
              <ListItemIcon>
                <UploadFileOutlined />
              </ListItemIcon>
              <ListItemText primary={orderType === OrderType.Quote ? "Attach File to Quote" : "Attach File to PO"} />
            </MenuItem>
          )}
        </Menu>
      </Box>
      <IconButton aria-label='expand row' size='small' onClick={() => row.toggleExpanded()} data-testid='expand-button'>
        {row.getIsExpanded() ? <KeyboardArrowUpFilledOutlined /> : <KeyboardArrowDownOutlined />}
      </IconButton>
    </Box>
  )
}
